export function addAttribute(name: string, value: string | undefined): string {
  if (value !== undefined) {
    if (value?.length > 0) {
      return `${name}: "${sanitize(value)}"`
    }

    return `${name}: ""`
  }

  return ''
}

export function replaceCreator(replacement: { addId?: string; removeId?: string }): string {
  if (replacement?.addId && replacement?.removeId && replacement?.addId !== replacement?.removeId) {
    return `replaceCreatorInput: {
      ${addAttribute('id_creator_to_add', replacement.addId)},
      ${addAttribute('id_creator_to_remove', replacement.removeId)}
    }`
  }

  return ``
}

export function stripHtml(value: string | undefined): string | undefined {
  if (value?.length) {
    let result = value
    let prev: string
    do {
      prev = result
      result = result.replaceAll(/<[^>]*>?/g, '')
    } while (result !== prev)
    return result
  }
  return value
}

/**
 * Strips `null` from a string
 */
export function stripNull(value: string | undefined): string | undefined {
  if (value?.length) {
    return value.replaceAll('null', ' ')
  }
  return value
}

export function sanitize(value: string | undefined): string | undefined {
  if (value?.length) {
    return value.replaceAll(/"+/g, String.raw`\"`)
  }

  return value
}

export function brEncodeAttribute(value: string | undefined): string | undefined {
  if (value?.length) {
    return value.replaceAll(/(\r\n|\r|\n)/g, '<br>')
  }

  return value
}

export function returnEncodeAttribute(value: string | undefined): string | undefined {
  if (value?.length) {
    return value.replaceAll(/(<br>|<br\/>)/g, '\r')
  }

  return value
}

export function escapeBackslash(value: string | undefined): string | undefined {
  if (value?.length) {
    return value.replaceAll('\\', '\\\\')
  }

  return value
}

export function getEncodedAttribute(name: string, value: string | undefined): string {
  if (value && value.length > 0) {
    return `${name}: "${encodeURI(value)}"`
  }
  return ``
}

export function addEncodedAttribute(name: string, value: string | undefined): string {
  if (value !== undefined) {
    if (value?.length > 0) {
      return `${name}: "${encodeURI(value)}"`
    }

    return `${name}: ""`
  }

  return ''
}

export function getSocialAccounts(socialAccounts: { platform: string; urlOrHandle: string }[] | undefined): string {
  if (socialAccounts !== undefined) {
    if (socialAccounts?.length > 0) {
      const accountEntries = socialAccounts.map(
        (socialAccount) => `{
        platform: ${socialAccount.platform},
        ${addAttribute('urlOrHandle', socialAccount.urlOrHandle)}
      }`
      )
      return `socialAccounts: [${accountEntries}]`
    }

    return `socialAccounts: []`
  }

  return ``
}

export function getTarget(cmsId: string, locale: string): string {
  const id = addAttribute('id', cmsId)
  const localeAttrib = addAttribute('locale', locale)
  if (localeAttrib?.length > 0) {
    return `_target: { ${id}, ${localeAttrib} },`
  }

  return `_target: { ${id} },`
}

export function getVisibilityRequest(statusValues: string[] | undefined): string {
  if (statusValues?.length) {
    return `visibilityRequest: {status: [${statusValues}]}`
  }

  return `visibilityRequest: {status: []}`
}

export function getIds(ids: string[] | undefined, key = 'ids'): string {
  if (ids?.length) {
    const quotedIds = ids.map((id) => `"${id}"`)
    return `${key}: [${quotedIds}]`
  }
  return ``
}

export function getCreatorIds(creators: string[] | undefined): string {
  if (creators?.length) {
    const quotedCreators = creators.map((creatorId) => `"${creatorId}"`)
    return `creators: [${quotedCreators}]`
  }
  return ``
}

export function getCreatorTypes(creatorTypes: string[] | undefined): string {
  if (creatorTypes?.length) {
    const quotedTypes = creatorTypes.map((type) => `"${type}"`)
    return `creatorTypes: [${quotedTypes}]`
  }

  return ``
}

export function getEnumAttribute(name: string, value: string | undefined): string {
  if (value !== undefined) {
    if (value?.length > 0) {
      // eslint-disable-next-line no-useless-concat
      return `${name}:` + `${value}`
    }

    return `${name}: ""`
  }

  return ``
}

export function getContactInformation(
  creator: { contactInformation?: { country: string; email: string } } | undefined
): string {
  if (creator?.contactInformation !== undefined) {
    return `contactInformation: {
        ${addAttribute('country', creator.contactInformation.country)}
        ${addAttribute('email', creator.contactInformation.email)}
      }`
  }

  return ''
}

export function getWorkflow(transition: boolean | undefined): string {
  if (transition) {
    return `_workflow: {action: TRANSITION, targetState: {next: true}}`
  }

  return ''
}

export function getPaginationData(paginationData: unknown): string {
  if (paginationData) {
    return `paginationData { ${[...(paginationData as unknown[])]} }`
  }

  return ''
}

export function getTargetHack(cmsObj: { id?: string; locale: string }): string {
  // If we have a session id we are doing an update.  Otherwise, it is a create
  let targetParam
  if (cmsObj?.id) {
    targetParam = getTarget(cmsObj.id, cmsObj.locale)
  } else {
    targetParam = addAttribute('contentLocale', cmsObj.locale)
  }

  return targetParam
}
